import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { validateEmail } from "#app/lib/custom-field-utils";
import {
  Badge,
  Box,
  Button,
  Dialog,
  DialogHeader,
  DialogPanel,
  DialogPopup,
  DialogTitle,
  Field,
  FieldLabel,
  Icon,
  ScrollArea,
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  Text,
  useIsMobile,
} from "@salestrak/ui";

import { EntityVisual } from "#app/components/entity";
import { JobTitleSelector } from "#app/components/entity-selectors/job-title-selector";
import { RoleSelector } from "#app/components/entity-selectors/role-selector";
import { useRolePool } from "#app/components/entity-selectors/hooks/use-role-pool";
import { TeamSelector } from "#app/components/entity-selectors/team-selector";
import { useYieldToEntityDetail } from "#app/components/entity";
import { useAppData } from "#app/lib/data";
import { useWorkspace } from "#app/lib/data/app-provider";
import { useInvitableRoles } from "#app/lib/hooks/use-invitable-roles";
import { useMemberMutations } from "#app/lib/hooks/use-member-mutations";
import { useMembersByEmail } from "#app/lib/hooks/use-members-by-email";
import { usePermission } from "#app/lib/hooks/use-permission";
import { memberStatusPrecedence } from "#app/lib/role-utils";
import type { Omittable } from "@salestrak/ui";

// ---------------------------------------------------------------------------
// InviteMembersDialog — the invite bar over a list of people.
//
// One Emails field. Enter, a comma, a space, a paste or a blur commits an
// address, and the person lands as a row below: the gray "invited"
// monogram, the email with a title line under it, then Role and Team as
// real select fields in fixed columns. Every row renders the same cells,
// always visible, so the list reads straight down. There are no batch
// defaults: a new row copies the previous row's Role and Team (the
// workspace default role and the host's team seed the first), so a bulk
// send is one change on the row that differs. Only Remove waits for hover
// (always shown on touch). No modes, no review step.
//
// A person who is already in the workspace is caught at add time, kept in the
// list so the reason is visible, and left out of the send. A person whose
// invite fails to send stays too, with the reason under their email, and the
// primary turns into a retry.
// ---------------------------------------------------------------------------

type InviteMembersDialogProps = Omittable<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: (count: number) => void;
  onError?: (message: string) => void;
  /** Team the bar opens with — the team-page invite seeds its own team here. */
  defaultTeamIds?: string[];
  /** Addresses the list opens with — a re-invite of a member who left seeds
   * theirs. Read once, on mount: a host that re-invites someone else mounts a
   * fresh dialog (key it on the member). */
  initialEmails?: string[];
}>;

/** A person in the list. `roleId` null = the workspace default role. */
interface Person {
  id: string;
  email: string;
  roleId: string | null;
  teamIds: string[];
  title: string | null;
  /** Already in the workspace — shown, never sent. `invited` can be resent. */
  existing: "member" | "invited" | null;
  /** The workspace user behind `existing`, for Resend. */
  existingUserId: string | null;
  /** Why the last send failed for this person. */
  error: string | null;
}

/** Separators that commit the draft, on typed input and paste alike. */
const SPLIT_PATTERN = /[\s,;]+/;

/** Row height, the slide-in's settled max-height. */
const ROW_HEIGHT = 56;
/** The list caps at five rows; past that it scrolls inside the dialog
 *  behind a scroll-edge fade instead of growing it. Nothing is reserved:
 *  an empty dialog is the bar and nothing else. */
const LIST_MAX_HEIGHT = ROW_HEIGHT * 5;
/** Fixed value columns, so Role and Team line up down the list and a long
 *  name truncates instead of pushing the email column around. */
const ROLE_COLUMN = 140;
const TEAM_COLUMN = 170;

/** What a person row reads for an address already in the workspace: an
 *  invite can be resent, a member is held back, and someone who left can be
 *  invited again. */
function existingFrom(
  row: { status: string | null | undefined } | undefined,
): Person["existing"] {
  if (!row || row.status === "left") return null;
  return row.status === "invited" ? "invited" : "member";
}

let personSeq = 0;
function makePerson(
  email: string,
  seed: Pick<Person, "roleId" | "teamIds"> = { roleId: null, teamIds: [] },
): Person {
  personSeq += 1;
  return {
    id: `p${personSeq}`,
    email,
    roleId: seed.roleId,
    teamIds: seed.teamIds,
    title: null,
    existing: null,
    existingUserId: null,
    error: null,
  };
}

function invitedEntity(email: string) {
  return {
    kind: "user" as const,
    id: email,
    user: { email, memberStatus: "invited" },
  };
}

export function InviteMembersDialog({
  open,
  onOpenChange,
  onSuccess,
  onError,
  defaultTeamIds,
  initialEmails,
}: InviteMembersDialogProps) {
  // The team pickers' "Team" row action navigates to a team page — a modal
  // left open would sit over it, so this dialog yields (the app convention
  // for modals whose content can open an entity detail). The draft is
  // dropped with it; the press is an explicit choice to leave.
  useYieldToEntityDetail(open, () => onOpenChange(false));

  // This door's promise is typing addresses, so the email bar carries
  // `autoFocus` on every device, as a create dialog's name field does. On
  // a phone the sheet host takes it from there: the keyboard rises with the
  // sheet, the sheet opens at its top stop, and the bar takes focus back
  // once the sheet has settled.
  const isMobile = useIsMobile();
  const { currentUser, members, teams } = useAppData();
  const { inviteMembers, resendInvite } = useMemberMutations();
  const { activeWorkspace } = useWorkspace();
  const workspaceId = activeWorkspace?.workspaceId ?? "";
  // The title picker's "…" editor: the viewer may rename or delete the
  // custom titles they created, or every custom title with manage.users.
  const canManageTitles = usePermission("manage.users");

  const { allowedRoleIds } = useInvitableRoles(workspaceId);
  const { pool: rolePool, defaultRoleId } = useRolePool({ allowedRoleIds });

  const inputRef = useRef<HTMLInputElement>(null);
  const [draft, setDraft] = useState("");
  // Addresses that could not land: held as red chips in the well, each
  // droppable, never silently dropped and never blocking the send.
  const [bad, setBad] = useState<string[]>([]);
  // A quiet note under the bar (a duplicate), not a fault.
  const [note, setNote] = useState<string | null>(null);
  const [listedPeople, setPeople] = useState<Person[]>(() =>
    (initialEmails ?? []).map((email) =>
      makePerson(email, { roleId: null, teamIds: defaultTeamIds ?? [] }),
    ),
  );
  // Rows that just landed: drawn collapsed for one frame, then let grow so
  // they slide into place instead of popping.
  const [entering, setEntering] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  // The row a duplicate pointed at, washed for a moment.
  const [flashId, setFlashId] = useState<string | null>(null);
  // Phone: the person whose sheet is open.
  const [editingId, setEditingId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [resending, setResending] = useState<string | null>(null);
  // Send pressed with an address still in the bar: it lands as a row first,
  // then the send goes out on the next render.
  const [sendAfterCommit, setSendAfterCommit] = useState(false);

  useEffect(() => {
    if (entering.size === 0) return;
    const frame = requestAnimationFrame(() => setEntering(new Set()));
    return () => cancelAnimationFrame(frame);
  }, [entering]);

  useEffect(() => {
    if (flashId === null) return;
    const timer = setTimeout(() => setFlashId(null), 1200);
    return () => clearTimeout(timer);
  }, [flashId]);

  // Who is already in the workspace, by address.
  const membersByEmail = useMemo(() => {
    const map = new Map<string, { userId: string; status: string | null }>();
    for (const m of members) {
      if (!m.email) continue;
      const key = m.email.toLowerCase();
      const held = map.get(key);
      if (!held || memberStatusPrecedence(m.memberStatus) < memberStatusPrecedence(held.status)) {
        map.set(key, { userId: m.userId, status: m.memberStatus ?? null });
      }
    }
    return map;
  }, [members]);

  // The roster is one page, so an address it cannot answer is asked of the
  // server (`useMembersByEmail`), and a row that turns out to be
  // in the workspace reads as such once the answer lands.
  const unansweredEmails = useMemo(
    () =>
      listedPeople
        .map((p) => p.email.toLowerCase())
        .filter((email) => !membersByEmail.has(email))
        .sort(),
    [listedPeople, membersByEmail],
  );
  const { byEmail: serverMembersByEmail, pending: lookupPending } =
    useMembersByEmail(unansweredEmails);
  const people = useMemo(
    () =>
      serverMembersByEmail.size === 0
        ? listedPeople
        : listedPeople.map((p) => {
            if (p.existing !== null) return p;
            const row = serverMembersByEmail.get(p.email.toLowerCase());
            const existing = existingFrom(row && { status: row.memberStatus });
            return row && existing ? { ...p, existing, existingUserId: row.userId } : p;
          }),
    [listedPeople, serverMembersByEmail],
  );

  const roleName = useCallback(
    (roleId: string | null) =>
      rolePool.items.find((item) => item.id === roleId)?.title ?? null,
    [rolePool],
  );
  const teamName = useCallback(
    (teamId: string) => teams.find((t) => t.teamId === teamId)?.teamName ?? null,
    [teams],
  );

  // Commit the draft: every valid, new address becomes a row; a bad one stays
  // in the bar with the reason under it, never silently dropped; a duplicate
  // stays too, with a quiet note, and its row is pointed at.
  const commit = useCallback(
    (text: string) => {
      const tokens = text.split(SPLIT_PATTERN).filter(Boolean);
      if (tokens.length === 0) return;
      const listed = new Map(people.map((p) => [p.email.toLowerCase(), p.id]));
      const added: Person[] = [];
      const invalid: string[] = [];
      const dupes: string[] = [];
      let pointAt: string | null = null;
      for (const token of tokens) {
        if (!validateEmail(token)) {
          invalid.push(token);
          continue;
        }
        const key = token.toLowerCase();
        const existingRow = listed.get(key);
        if (existingRow) {
          dupes.push(token);
          pointAt = existingRow;
          continue;
        }
        // A new row copies the row before it; the first takes the host's
        // team seed and the workspace default role.
        const previous = added[added.length - 1] ?? people[people.length - 1];
        const person = makePerson(
          token,
          previous
            ? { roleId: previous.roleId, teamIds: previous.teamIds }
            : { roleId: null, teamIds: defaultTeamIds ?? [] },
        );
        const known = membersByEmail.get(key);
        person.existing = existingFrom(known);
        if (person.existing) person.existingUserId = known?.userId ?? null;
        listed.set(key, person.id);
        added.push(person);
      }
      if (added.length > 0) {
        setPeople((prev) => [...prev, ...added]);
        setEntering(new Set(added.map((p) => p.id)));
      }
      if (invalid.length > 0) {
        setBad((prev) => {
          const seen = new Set(prev.map((t) => t.toLowerCase()));
          return [
            ...prev,
            ...invalid.filter((t) => {
              if (seen.has(t.toLowerCase())) return false;
              seen.add(t.toLowerCase());
              return true;
            }),
          ];
        });
      }
      setDraft(dupes.join(" "));
      setNote(
        dupes.length === 0
          ? null
          : dupes.length === 1
            ? "Already in the list."
            : `${dupes.length} are already in the list.`,
      );
      setFlashId(pointAt);
    },
    [people, membersByEmail, defaultTeamIds],
  );

  const handleDraftChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const next = event.target.value;
      // A separator in the draft commits everything before the last one and
      // keeps the trailing fragment as the live draft.
      if (new RegExp(SPLIT_PATTERN.source, "g").test(next)) {
        const parts = next.split(SPLIT_PATTERN);
        const remainder = parts.pop() ?? "";
        const done = parts.filter(Boolean).join(" ");
        if (done) {
          commit(done);
          // `commit` may have parked a bad token in the draft; keep the
          // fragment the user is still typing after it.
          setDraft((parked) => [parked, remainder].filter(Boolean).join(" "));
          return;
        }
        setDraft(remainder);
        return;
      }
      setDraft(next);
      if (note) setNote(null);
    },
    [commit, note],
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === "Enter") {
        event.preventDefault();
        if (draft.trim()) commit(draft);
        return;
      }
      // Backspace on an empty draft takes the last bad chip back into the
      // field as text, so the next keystroke edits it instead of retyping.
      if (event.key === "Backspace" && draft === "" && bad.length > 0) {
        event.preventDefault();
        const last = bad[bad.length - 1]!;
        setBad((prev) => prev.slice(0, -1));
        setDraft(last);
      }
    },
    [draft, bad, commit],
  );

  const handlePaste = useCallback(
    (event: React.ClipboardEvent<HTMLInputElement>) => {
      // Text inputs mangle multi-line pastes (line breaks collapse), so the
      // paste path tokenizes the raw clipboard text itself.
      const text = event.clipboardData.getData("text");
      if (!SPLIT_PATTERN.test(text)) return;
      event.preventDefault();
      commit(`${draft} ${text}`);
    },
    [commit, draft],
  );

  const handleBlur = useCallback(() => {
    if (draft.trim()) commit(draft);
  }, [draft, commit]);

  const updatePerson = useCallback((id: string, patch: Partial<Person>) => {
    setPeople((prev) =>
      prev.map((p) => (p.id === id ? { ...p, ...patch } : p)),
    );
  }, []);

  const removePerson = useCallback((id: string) => {
    setPeople((prev) => prev.filter((p) => p.id !== id));
    setEditingId((current) => (current === id ? null : current));
  }, []);

  const handleResend = useCallback(
    async (key: string, userId: string) => {
      if (resending) return;
      setResending(key);
      await resendInvite(userId);
      setResending(null);
    },
    [resendInvite, resending],
  );

  const sendable = useMemo(
    () => people.filter((p) => p.existing === null),
    [people],
  );
  const heldBack = people.length - sendable.length;
  const hasFailures = people.some((p) => p.error !== null);
  // What the bar would add if sent now: valid addresses not already listed.
  const draftAdds = useMemo(() => {
    const listed = new Set(people.map((p) => p.email.toLowerCase()));
    return draft
      .split(SPLIT_PATTERN)
      .filter((t) => t && validateEmail(t) && !listed.has(t.toLowerCase()))
      .length;
  }, [draft, people]);

  // Addresses in the bar that are already rows (letter case ignored). Derived
  // from the draft, so the note stays while a parked duplicate is still there
  // — an edit to the draft used to clear the note and leave the text unexplained.
  const draftDupes = useMemo(() => {
    const listed = new Set(people.map((p) => p.email.toLowerCase()));
    return draft
      .split(SPLIT_PATTERN)
      .filter((t) => t && validateEmail(t) && listed.has(t.toLowerCase()))
      .length;
  }, [draft, people]);
  const dupeNote =
    draftDupes === 0
      ? note
      : draftDupes === 1
        ? "Already in the list."
        : `${draftDupes} are already in the list.`;

  const canSubmit =
    !submitting &&
    !lookupPending &&
    defaultRoleId !== null &&
    (sendable.length > 0 || draftAdds > 0);

  const dropBad = useCallback((token: string) => {
    setBad((prev) => prev.filter((t) => t !== token));
  }, []);

  const handleClose = useCallback(() => {
    setDraft("");
    setBad([]);
    setNote(null);
    setPeople([]);
    setEditingId(null);
    setSendAfterCommit(false);
    onOpenChange(false);
  }, [onOpenChange]);

  const send = useCallback(async () => {
    if (
      submitting ||
      lookupPending ||
      defaultRoleId === null ||
      sendable.length === 0
    ) {
      return;
    }

    setSubmitting(true);
    const { succeededEmails, failures, error: sendError } = await inviteMembers(
      sendable.map((p) => ({
        email: p.email,
        roleId: p.roleId ?? defaultRoleId,
        teamIds: p.teamIds,
        title: p.title,
      })),
      "invite_dialog",
    );
    setSubmitting(false);

    if (sendError) {
      onError?.(sendError);
      return;
    }
    if (succeededEmails.length > 0) onSuccess?.(succeededEmails.length);
    if (failures.length > 0) {
      onError?.(failures.map((f) => `${f.email}: ${f.reason}`).join("; "));
    }
    const sent = new Set(succeededEmails.map((e) => e.toLowerCase()));
    const reasons = new Map(
      failures.map((f) => [f.email.toLowerCase(), f.reason]),
    );
    // Successes leave; a failed row stays with its reason so the user never
    // reconstructs what happened from a toast.
    const remaining = people
      .filter((p) => !sent.has(p.email.toLowerCase()))
      .map((p) => ({
        ...p,
        error: reasons.get(p.email.toLowerCase()) ?? p.error,
      }));
    setPeople(remaining);
    if (remaining.length === 0) handleClose();
  }, [
    submitting,
    lookupPending,
    defaultRoleId,
    people,
    sendable,
    inviteMembers,
    onSuccess,
    onError,
    handleClose,
  ]);

  // Send with an address still in the bar: it lands first. A duplicate
  // stays parked and the send waits for it; a bad address becomes a chip
  // and does not hold the send up.
  const handlePrimary = useCallback(() => {
    if (draft.trim()) {
      commit(draft);
      setSendAfterCommit(true);
      return;
    }
    void send();
  }, [draft, commit, send]);

  useEffect(() => {
    if (!sendAfterCommit) return;
    if (draft.trim()) {
      // A duplicate stayed parked — the bar says so.
      setSendAfterCommit(false);
      inputRef.current?.focus();
      return;
    }
    if (lookupPending) return;
    setSendAfterCommit(false);
    void send();
  }, [sendAfterCommit, draft, lookupPending, send]);

  const count = sendable.length + draftAdds;
  const sendLabel = hasFailures
    ? count === 1
      ? "Retry 1 invite"
      : `Retry ${count} invites`
    : count > 1
      ? `Send ${count} invites`
      : "Send invite";

  const bar = (
    <Box>
      {/* InputGroup's input is not a Field.Control, so the label names it by
          id; the Field root is still what lets FieldLabel render. */}
      <Field>
        <FieldLabel htmlFor="invite-emails">Emails</FieldLabel>
        <InputGroup>
        {bad.length > 0 && (
          <InputGroupAddon align="inline-start">
            {bad.map((token) => (
              <Badge key={token} size="chip" color="destructive">
                <Box direction="row" align="center" gap="xs">
                  <Text size="xs" truncate={1}>
                    {token}
                  </Text>
                  <Box
                    as="button"
                    type="button"
                    aria-label={`Remove ${token}`}
                    onClick={() => dropBad(token)}
                  >
                    <Icon name="x" size="xs" />
                  </Box>
                </Box>
              </Badge>
            ))}
          </InputGroupAddon>
        )}
        <InputGroupInput
          ref={inputRef}
          id="invite-emails"
          autoFocus
          type="text"
          inputMode="email"
          autoComplete="off"
          autoCapitalize="none"
          spellCheck={false}
          aria-label="Add people by email"
          aria-invalid={bad.length > 0 ? true : undefined}
          placeholder="Enter emails, separated by commas or spaces"
          value={draft}
          onChange={handleDraftChange}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          onBlur={handleBlur}
          disabled={submitting}
        />
        </InputGroup>
        {bad.length > 0 ? (
          <Text size="xs" variant="destructive">
            {bad.length === 1
              ? `${bad[0]} isn't a valid address`
              : `${bad.length} aren't valid addresses`}
          </Text>
        ) : dupeNote ? (
          <Text size="xs" variant="muted">
            {dupeNote}
          </Text>
        ) : null}
      </Field>
    </Box>
  );

  /** The line under a person's email that says why they will not be sent. */
  const statusLine = (person: Person) =>
    person.error ? (
      <Text size="xs" variant="destructive" truncate={1}>
        Couldn't send · {person.error}
      </Text>
    ) : person.existing === "member" ? (
      <Text size="xs" variant="warning">
        Already a member
      </Text>
    ) : person.existing === "invited" ? (
      <Text size="xs" variant="warning">
        Already invited
      </Text>
    ) : null;

  /** Phone: one line of the person's role, team and title. */
  const summaryLine = (person: Person) => {
    const parts = [
      roleName(person.roleId ?? defaultRoleId),
      ...person.teamIds.map(teamName),
      person.title,
    ].filter((v): v is string => !!v);
    return (
      <Text size="xs" variant="muted" truncate={1}>
        {parts.join(" · ")}
      </Text>
    );
  };

  const rows = people.map((person, index) => {
        const held = person.existing !== null;
        // The row has no labels, so each control says what it does here.
        const rolePicker = (
          <RoleSelector
            aria-label={`Role for ${person.email}`}
            placeholder="Choose a role"
            value={person.roleId ?? defaultRoleId}
            onValueChange={(next) => updatePerson(person.id, { roleId: next })}
            allowedRoleIds={allowedRoleIds}
            trigger="field"
            fill
          />
        );
        const teamPicker = (
          <TeamSelector
            aria-label={`Team for ${person.email}`}
            placeholder="Add to a team"
            value={person.teamIds}
            onValueChange={(next) => updatePerson(person.id, { teamIds: next })}
            plain
            fill
          />
        );
        const titlePicker = (
          <JobTitleSelector
            value={person.title}
            onValueChange={(title) => updatePerson(person.id, { title })}
            currentUserId={currentUser?.userId ?? null}
            canManage={canManageTitles}
            placeholder="Add a title"
            size="xs"
            shape="square"
          />
        );
        // Email over the title line, always both. A held-back row says why
        // under its email instead; a phone row carries a one-line summary
        // and opens the person's sheet.
        const identity = (
          <Box flex={1} minWidth={0} gap="2xs" justify="center">
            <Text size="sm" weight="medium" truncate={1}>
              {person.email}
            </Text>
            {statusLine(person) ??
              (isMobile ? (
                summaryLine(person)
              ) : (
                <Box direction="row" marginLeftNegative="sm" minWidth={0} shrink>
                  {titlePicker}
                </Box>
              ))}
          </Box>
        );
        // A held-back row keeps the columns as empty cells so the list stays
        // straight; its one action (Resend) rides the trailing slot.
        const trailing = held ? (
          <>
            <Box width={ROLE_COLUMN} shrink={false} />
            <Box width={TEAM_COLUMN} shrink={false} direction="row" justify="end">
              {person.existing === "invited" && person.existingUserId ? (
                <Box hoverReveal="row" revealOnTouch>
                  <Button
                    variant="ghost"
                    size="sm"
                    shape="square"
                    loading={resending === person.id}
                    onClick={() => handleResend(person.id, person.existingUserId!)}
                  >
                    Resend
                  </Button>
                </Box>
              ) : null}
            </Box>
          </>
        ) : (
          <>
            <Box width={ROLE_COLUMN} shrink={false}>
              {rolePicker}
            </Box>
            <Box width={TEAM_COLUMN} shrink={false}>
              {teamPicker}
            </Box>
          </>
        );
        const remove = (
          <Box shrink={false}>
            <Button
              variant="ghost"
              size="icon-sm"
              shape="square"
              aria-label={`Remove ${person.email}`}
              onClick={() => removePerson(person.id)}
            >
              <Icon name="x" size="sm" />
            </Button>
          </Box>
        );
        const avatar = (
          <Box shrink={false}>
            {/* The gray invited monogram: this person is on their way, not a
                member yet — the roster's Invited treatment. */}
            <EntityVisual entity={invitedEntity(person.email)} size="md" decorative />
          </Box>
        );
        const body = isMobile ? (
          // The row is the way into the person's sheet; Remove lives there.
          <Box
            as="button"
            type="button"
            direction="row"
            align="center"
            gap="md"
            fill="width"
            minHeight={ROW_HEIGHT}
            cursor="pointer"
            onClick={() => setEditingId(person.id)}
            aria-label={`Edit ${person.email}`}
          >
            {avatar}
            {identity}
            <Icon name="chevron-right" size="sm" color="faint" />
          </Box>
        ) : (
          <Box
            group="row"
            direction="row"
            align="center"
            gap="sm"
            minHeight={ROW_HEIGHT}
            marginXNegative="sm"
            paddingX="sm"
            rounded="md"
            background={flashId === person.id ? "muted" : undefined}
            transition="colors"
            duration={300}
          >
            {avatar}
            {identity}
            {trailing}
            {remove}
          </Box>
        );
        return (
          <Box
            key={person.id}
            overflow="hidden"
            transition="all"
            duration={300}
            maxHeight={entering.has(person.id) ? 0 : ROW_HEIGHT * 2}
            opacity={entering.has(person.id) ? 0 : 100}
          >
            <Box border={index < people.length - 1 ? "bottom" : undefined}>
              {body}
            </Box>
          </Box>
        );
      });

  const peopleLabel = people.length === 1 ? "1 person" : `${people.length} people`;
  const countLabel =
    heldBack > 0
      ? `${peopleLabel} · ${heldBack === 1 ? "1 won't be sent" : `${heldBack} won't be sent`}`
      : peopleLabel;

  const editing = people.find((p) => p.id === editingId) ?? null;

  return (
    <>
    <Dialog
      open={open}
      // Closing from the × or a dismiss drops the draft the way Cancel did,
      // so a reopen starts clean.
      onOpenChange={(next) => (next ? onOpenChange(true) : handleClose())}
      sheetSize="full"
    >
      {/* The popup owns its own surface and padding. */}
      <DialogPopup
        size="2xl"
        dialogOptions={{
          primary: {
            label: sendLabel,
            onClick: handlePrimary,
            disabled: !canSubmit,
            loading: submitting,
          },
          // The header's × is the way out; a Cancel beside Send is a second
          // door to the same place.
          cancel: false,
        }}
      >
        {/* On the sheet the commit sits on the title row as its word, always
            in reach above the keyboard. */}
        <DialogHeader>
          <DialogTitle>Invite Members</DialogTitle>
        </DialogHeader>
        {/* DialogPanel owns the body gutters and the header/footer spacing
            rules — a bare padded Box leaves no gap above the footer. */}
        <DialogPanel>
          <Box gap="sm">
            {bar}
            {people.length > 0 && (
              <Box>
                <Box direction="row" align="center" height={32} shrink={false}>
                  <Text size="xs" weight="medium" variant="faint">
                    {countLabel}
                  </Text>
                </Box>
                {/* Capped at five rows: a long paste scrolls inside, the
                    overflow edge fading, instead of growing the dialog. */}
                <Box maxHeight={LIST_MAX_HEIGHT} minHeight={0}>
                  <ScrollArea scrollFade>
                    <Box>{rows}</Box>
                  </ScrollArea>
                </Box>
              </Box>
            )}
          </Box>
        </DialogPanel>
      </DialogPopup>

      {/* Phone: one person's settings, a sheet over the receded list. */}
      {isMobile && (
        <Dialog
          open={editing !== null}
          onOpenChange={(next) => {
            if (!next) setEditingId(null);
          }}
        >
          <DialogPopup
            dialogOptions={{
              primary: { label: "Done", onClick: () => setEditingId(null) },
              cancel: false,
            }}
          >
            <DialogHeader>
              <DialogTitle>{editing?.email ?? ""}</DialogTitle>
            </DialogHeader>
            <DialogPanel>
              {editing && (
                <Box gap="md">
                  <Field>
                    <FieldLabel>Role</FieldLabel>
                    <RoleSelector
                      aria-label="Role"
                      placeholder="Choose a role"
                      value={editing.roleId ?? defaultRoleId}
                      onValueChange={(next) => updatePerson(editing.id, { roleId: next })}
                      allowedRoleIds={allowedRoleIds}
                      trigger="field"
                      fill
                    />
                  </Field>
                  <Field>
                    <FieldLabel>Team</FieldLabel>
                    <TeamSelector
                      aria-label="Team"
                      placeholder="Add to a team"
                      value={editing.teamIds}
                      onValueChange={(next) => updatePerson(editing.id, { teamIds: next })}
                      plain
                      fill
                    />
                  </Field>
                  <Field>
                    <FieldLabel>Title</FieldLabel>
                    {/* Each picker is a sheet stacked over this one (the
                        selector's phone default), never a popover. */}
                    <JobTitleSelector
                      value={editing.title}
                      onValueChange={(title) => updatePerson(editing.id, { title })}
                      currentUserId={currentUser?.userId ?? null}
                      canManage={canManageTitles}
                      placeholder="Add a title"
                      trigger="field"
                    />
                  </Field>
                  <Box direction="row" marginLeftNegative="sm">
                    <Button
                      variant="ghost"
                      color="destructive"
                      size="sm"
                      shape="square"
                      onClick={() => removePerson(editing.id)}
                    >
                      Remove from invite
                    </Button>
                  </Box>
                </Box>
              )}
            </DialogPanel>
          </DialogPopup>
        </Dialog>
      )}
    </Dialog>
    </>
  );
}
